<template>
  <div class="pdf-batch-processor">
    <header class="head">
      <h3>PDF 批量处理货件地址</h3>
      <p class="sub">选择语言后上传 PDF 或 zip，自动遮挡地址并打包下载</p>
    </header>

    <!-- 语言选择 -->
    <section class="block lang-block">
      <p class="block-title">选择文档语言</p>
      <el-radio-group v-model="selectedLang">
        <el-radio value="zh">中文（遮挡在左边）</el-radio>
        <el-radio value="en">英文（遮挡在右边）</el-radio>
      </el-radio-group>
    </section>

    <!-- 方式一：多选 PDF -->
    <section class="block upload-block">
      <p class="block-title">方式一：选择一组 PDF</p>
      <label class="drop-zone" :class="{ dragging }"
             @dragover.prevent="dragging = true"
             @dragleave.prevent="dragging = false"
             @drop.prevent="handleDrop">
        <input type="file" accept="application/pdf,.pdf" multiple @change="handlePdfsChange" hidden />
        <span class="drop-icon">＋</span>
        <span>点击选择 或 拖拽 PDF 到此处</span>
        <small v-if="pickedPdfCount">已选 {{ pickedPdfCount }} 个 PDF</small>
      </label>
    </section>

    <!-- 方式二：上传 zip -->
    <section class="block upload-block">
      <p class="block-title">方式二：上传一个 zip 压缩包（内部为 PDF）</p>
      <label class="drop-zone" :class="{ dragging }"
             @dragover.prevent="dragging = true"
             @dragleave.prevent="dragging = false"
             @drop.prevent="handleDropZip">
        <input type="file" accept=".zip,application/zip" @change="handleZipChange" hidden />
        <span class="drop-icon">＋</span>
        <span>点击选择 或 拖拽 zip 到此处</span>
      </label>
    </section>

    <!-- 进度 -->
    <div v-if="processing" class="progress-block">
      <p class="progress-text">正在处理（{{ doneCount }} / {{ totalCount }}）…</p>
      <el-progress
          :percentage="percent"
          :stroke-width="8"
          :show-text="false"
          color="#4fd1ff"
      />
    </div>

    <!-- 失败列表 -->
    <div v-if="failedFiles.length" class="failed-block">
      <p class="failed-title">以下文件处理失败：</p>
      <ul>
        <li v-for="(f, i) in failedFiles" :key="i">{{ f }}</li>
      </ul>
    </div>

    <!-- 结果 -->
    <div v-if="downloadUrl" class="result-section">
      <p>处理完成，点击下载：</p>
      <el-button type="primary" @click="downloadFile">下载 zip</el-button>
    </div>
  </div>
</template>
<script>
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { PDFDocument, rgb } from 'pdf-lib';

export default {
  name: 'PdfBatchProcessor',
  data() {
    return {
      selectedLang: 'zh',
      processing: false,
      doneCount: 0,
      totalCount: 0,
      downloadUrl: '',
      zipFileName: 'processed_pdfs.zip',
      pickedPdfCount: 0,
      dragging: false,
      failedFiles: [],
    };
  },
  computed: {
    percent() {
      if (!this.totalCount) return 0;
      return Math.round((this.doneCount / this.totalCount) * 100);
    },
  },
  beforeUnmount() {
    this.revokeDownloadUrl();
  },
  methods: {
    /* ---------- 方式一：多选 PDF ---------- */
    async handlePdfsChange(e) {
      const files = Array.from(e.target.files || []).filter(
          (f) => f.type === 'application/pdf' || /\.pdf$/i.test(f.name)
      );
      e.target.value = ''; // 先清空，避免残留
      if (!files.length) {
        this.$message.warning('请选择 PDF 文件');
        return;
      }
      this.pickedPdfCount = files.length;
      await this.processPdfs(files);
    },

    /* ---------- 拖拽 PDF ---------- */
    async handleDrop(e) {
      this.dragging = false;
      const files = Array.from(e.dataTransfer.files || []).filter(
          (f) => f.type === 'application/pdf' || /\.pdf$/i.test(f.name)
      );
      if (!files.length) {
        this.$message.warning('请拖入 PDF 文件');
        return;
      }
      this.pickedPdfCount = files.length;
      await this.processPdfs(files);
    },

    /* ---------- 方式二：zip ---------- */
    async handleZipChange(e) {
      const file = e.target.files && e.target.files[0];
      e.target.value = '';
      if (!file) return;
      if (!/\.zip$/i.test(file.name)) {
        this.$message.error('请上传 zip 压缩包');
        return;
      }
      await this.processZip(file);
    },

    async handleDropZip(e) {
      this.dragging = false;
      const file = e.dataTransfer.files && e.dataTransfer.files[0];
      if (!file) return;
      if (!/\.zip$/i.test(file.name)) {
        this.$message.error('请拖入 zip 压缩包');
        return;
      }
      await this.processZip(file);
    },

    async processZip(file) {
      this.processing = true;
      this.doneCount = 0;
      this.totalCount = 0;
      this.failedFiles = [];
      this.revokeDownloadUrl();

      try {
        const zip = await JSZip.loadAsync(file);
        const entries = Object.values(zip.files).filter(
            (f) => !f.dir && /\.pdf$/i.test(f.name)
        );
        if (!entries.length) {
          this.$message.warning('压缩包内没有 PDF 文件');
          return;
        }
        this.totalCount = entries.length;

        const pdfFiles = [];
        for (const entry of entries) {
          const blob = await entry.async('blob');
          const name = entry.name.split('/').pop();
          pdfFiles.push(new File([blob], name, { type: 'application/pdf' }));
        }
        await this.processPdfs(pdfFiles, false);
      } catch (err) {
        console.error('解压失败', err);
        this.$message.error('zip 解压失败：' + (err.message || err));
      } finally {
        this.processing = false;
      }
    },

    /* ---------- 统一处理 ---------- */
    async processPdfs(files, manageState = true) {
      if (manageState) {
        this.processing = true;
        this.doneCount = 0;
        this.totalCount = files.length;
        this.failedFiles = [];
        this.revokeDownloadUrl();
      }

      const outZip = new JSZip();
      this.failedFiles = [];

      for (const file of files) {
        try {
          const bytes = await this.coverAddressOnPdf(file);
          const outName = file.name.replace(/\.pdf$/i, '_marked.pdf');
          outZip.file(outName, bytes);
        } catch (err) {
          console.error('处理失败：' + file.name, err);
          this.failedFiles.push(file.name);
        } finally {
          this.doneCount++;
        }
      }

      const successCount = files.length - this.failedFiles.length;
      if (successCount === 0) {
        this.$message.error('没有 PDF 处理成功');
        this.processing = false;
        return;
      }

      try {
        const zipBlob = await outZip.generateAsync({ type: 'blob' });
        this.revokeDownloadUrl();
        this.downloadUrl = URL.createObjectURL(zipBlob);
        this.zipFileName = 'processed_pdfs.zip';
        this.$message.success(
            `处理完成：成功 ${successCount} 个` +
            (this.failedFiles.length ? `，失败 ${this.failedFiles.length} 个` : '')
        );
      } catch (err) {
        console.error('打包失败', err);
        this.$message.error('打包失败：' + (err.message || err));
      } finally {
        this.processing = false;
      }
    },

    /* ---------- 在 PDF 上覆盖地址 ---------- */
    async coverAddressOnPdf(file) {
      const arrayBuffer = await file.arrayBuffer();
      const pdfDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
      const pages = pdfDoc.getPages();

      for (const page of pages) {
        const { height } = page.getSize();
        const x = this.selectedLang === 'en' ? 161 : 30;
        const y = height - 82;

        page.drawRectangle({
          x,
          y,
          width: 121,
          height: 8,
          color: rgb(1, 1, 1), // 白色遮盖
        });
      }
      return await pdfDoc.save();
    },

    revokeDownloadUrl() {
      if (this.downloadUrl) {
        URL.revokeObjectURL(this.downloadUrl);
        this.downloadUrl = '';
      }
    },

    downloadFile() {
      if (!this.downloadUrl) {
        this.$message.warning('暂无可下载文件');
        return;
      }
      saveAs(this.downloadUrl, this.zipFileName);
    },
  },
};
</script>

<style scoped>
.pdf-batch-processor {
  max-width: 760px;
  margin: 32px auto;
  padding: 24px;
  box-sizing: border-box;
  border-radius: 16px;
  /* 深科技蓝底,与全局一致 */
  background:
      radial-gradient(120% 60% at 50% 0%, rgba(42, 74, 184, 0.18) 0%, transparent 60%),
      #0a1230;
  color: #dbe6ff;
}

.head h3 {
  margin: 0 0 6px;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 1px;
  background: linear-gradient(90deg, #fff, #a9c2ff);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}
.head .sub {
  margin: 0 0 20px;
  font-size: 13px;
  color: rgba(190, 210, 255, 0.65);
  letter-spacing: 0.5px;
}

/* 通用块 */
.block {
  margin-bottom: 20px;
  padding: 16px;
  border-radius: 12px;
  background: rgba(22, 38, 99, 0.4);
  border: 1px solid rgba(120, 160, 255, 0.18);
}

.block-title {
  margin: 0 0 12px;
  font-size: 14px;
  color: rgba(190, 210, 255, 0.8);
  letter-spacing: 1px;
}

/* 拖拽区 */
.drop-zone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px 16px;
  border-radius: 10px;
  border: 1px dashed rgba(120, 160, 255, 0.45);
  background: rgba(10, 18, 48, 0.5);
  cursor: pointer;
  text-align: center;
  color: rgba(200, 216, 255, 0.75);
  font-size: 14px;
  transition: border-color 0.25s, background 0.25s, box-shadow 0.25s;
}
.drop-zone:hover,
.drop-zone.dragging {
  border-color: rgba(79, 209, 255, 0.85);
  background: rgba(22, 38, 99, 0.65);
  box-shadow: 0 0 14px rgba(79, 141, 255, 0.35);
}
.drop-zone small {
  color: rgba(160, 190, 255, 0.65);
}
.drop-icon {
  font-size: 24px;
  color: #4fd1ff;
  line-height: 1;
}

/* 进度 */
.progress-block {
  margin: 20px 0;
  padding: 16px;
  border-radius: 12px;
  background: rgba(22, 38, 99, 0.35);
  border: 1px solid rgba(120, 160, 255, 0.18);
}
.progress-text {
  margin: 0 0 10px;
  font-size: 13px;
  color: #9ec2ff;
  letter-spacing: 0.5px;
}

/* 失败列表 */
.failed-block {
  margin: 16px 0;
  padding: 14px 16px;
  border-radius: 12px;
  background: rgba(255, 86, 120, 0.12);
  border: 1px solid rgba(255, 86, 120, 0.4);
  color: #ffb3c2;
  font-size: 13px;
}
.failed-title {
  margin: 0 0 6px;
  font-weight: 600;
}
.failed-block ul {
  margin: 0;
  padding-left: 18px;
}

/* 结果区 */
.result-section {
  margin-top: 24px;
  padding: 16px;
  border-radius: 12px;
  background: rgba(79, 141, 255, 0.12);
  border-left: 4px solid #4fd1ff;
  color: #dbe6ff;
}
.result-section p {
  margin: 0 0 10px;
}

/* Element Plus 在深底上的微调 */
:deep(.el-radio) {
  color: rgba(220, 230, 255, 0.85);
}
:deep(.el-radio__input.is-checked + .el-radio__label) {
  color: #4fd1ff;
}
:deep(.el-radio__input.is-checked .el-radio__inner) {
  background: #4fd1ff;
  border-color: #4fd1ff;
}
:deep(.el-progress-bar__outer) {
  background: rgba(120, 160, 255, 0.15);
}

/* 响应式 */
@media (max-width: 640px) {
  .pdf-batch-processor { margin: 16px; padding: 16px; }
  .head h3 { font-size: 18px; }
}
</style>